import React, { useMemo, useState } from "react";
import { Container, Card, Row as BootstrapRow, Col as BootstrapCol, Button, Alert } from "react-bootstrap";
import { Table, Select, DatePicker, Form, Tag } from "antd";
import {
  FilterOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import FormItem from "@/common/FormItem/FormItem";
import type { ColumnType } from "antd/es/table/interface";
import PageTitle from "@/common/PageTitle/PageTitle";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import dayjs from "dayjs";
import styles from "./live-plays-tracker.module.scss";
import {
  LivePlayFilters,
  LivePlayRow,
  useGetLivePlayFilterOptionsQuery,
  useGetLivePlaysQuery,
} from "@/features/live-plays/livePlaysApi";

const POLL_MS = 4000;

const columns: ColumnType<LivePlayRow>[] = [
  {
    title: "Status",
    dataIndex: "status",
    width: 110,
    render: (status: string, record) => (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          color:
            status === "playing"
              ? "#e21e26"
              : status === "paused" || status === "stale"
                ? "#f7b731"
                : "#52c41a",
        }}
      >
        {status === "playing" && <PlayCircleOutlined spin />}
        {status === "paused" && <span>⏸</span>}
        {(status || "unknown").charAt(0).toUpperCase() + (status || "unknown").slice(1)}
        {record.synthetic ? (
          <Tag color="orange" style={{ marginLeft: 6 }}>
            SYNTHETIC
          </Tag>
        ) : null}
      </span>
    ),
  },
  {
    title: "Client",
    dataIndex: "clientName",
    width: 160,
    render: (_: string, record) => record.clientName || record.masterAccountName || "—",
  },
  {
    title: "Device",
    dataIndex: "deviceName",
    width: 170,
    render: (name: string, record) => (
      <div>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <div style={{ fontSize: 11, color: "#888" }}>
          {[record.locationName, record.zoneName].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
    ),
  },
  {
    title: "Song Title",
    dataIndex: "songTitle",
    sorter: (a, b) => a.songTitle.localeCompare(b.songTitle),
    width: 200,
  },
  {
    title: "Artist",
    dataIndex: "artist",
    sorter: (a, b) => a.artist.localeCompare(b.artist),
    width: 150,
  },
  {
    title: "Channel",
    dataIndex: "channel",
    width: 180,
  },
  {
    title: "Started At",
    dataIndex: "playedAt",
    sorter: (a, b) => dayjs(a.playedAt).unix() - dayjs(b.playedAt).unix(),
    width: 180,
    render: (date: string) => (date ? dayjs(date).format("MM/DD/YYYY hh:mm:ss A") : "—"),
  },
  {
    title: "Duration",
    dataIndex: "duration",
    width: 100,
  },
];

const LivePlaysTracker: React.FC = () => {
  const [filters, setFilters] = useState<LivePlayFilters>({});
  const [resetCounter, setResetCounter] = useState(0);

  const queryFilters = useMemo(() => {
    const next: LivePlayFilters = {};
    if (filters.brandId) next.brandId = filters.brandId;
    if (filters.clientId) next.clientId = filters.clientId;
    if (filters.locationId) next.locationId = filters.locationId;
    if (filters.zoneId) next.zoneId = filters.zoneId;
    if (filters.deviceId) next.deviceId = filters.deviceId;
    if (filters.song) next.song = filters.song;
    if (filters.artist) next.artist = filters.artist;
    if (filters.channel) next.channel = filters.channel;
    if (filters.date) next.date = filters.date;
    return next;
  }, [filters]);

  const {
    data: liveData,
    isFetching,
    isError,
    error,
    fulfilledTimeStamp,
  } = useGetLivePlaysQuery(queryFilters, {
    pollingInterval: POLL_MS,
    refetchOnMountOrArgChange: true,
  });

  const { data: filterOptions } = useGetLivePlayFilterOptionsQuery({
    brandId: filters.brandId,
    clientId: filters.clientId,
    locationId: filters.locationId,
    zoneId: filters.zoneId,
  });

  const plays = liveData?.items || [];
  const metrics = liveData?.metrics || {
    activeDevices: 0,
    nowPlaying: 0,
    uniqueArtists: 0,
    activeChannels: 0,
  };
  const hasSynthetic = plays.some((p) => p.synthetic);
  const isConnected = !isError && typeof fulfilledTimeStamp === "number";

  const handleHierarchyChange = (
    key: "brandId" | "clientId" | "locationId" | "zoneId" | "deviceId",
    value: string | undefined,
  ) => {
    setFilters((prev) => {
      const next: LivePlayFilters = { ...prev, [key]: value || undefined };
      if (key === "brandId") {
        next.clientId = undefined;
        next.locationId = undefined;
        next.zoneId = undefined;
        next.deviceId = undefined;
      } else if (key === "clientId") {
        next.locationId = undefined;
        next.zoneId = undefined;
        next.deviceId = undefined;
      } else if (key === "locationId") {
        next.zoneId = undefined;
        next.deviceId = undefined;
      } else if (key === "zoneId") {
        next.deviceId = undefined;
      }
      return next;
    });
  };

  const handleFilterChange = (key: keyof LivePlayFilters, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const handleReset = () => {
    setFilters({});
    setResetCounter((prev) => prev + 1);
  };

  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <Card.Title style={{ display: "flex", justifyContent: "space-between" }}>
            <PageTitle title="Live Plays Tracker" />
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  color: isConnected ? "#52c41a" : "#999",
                  fontWeight: 500,
                }}
              >
                <WifiOutlined style={{ fontSize: 16 }} />
                {isConnected ? "Live" : isError ? "Disconnected" : "Connecting…"}
              </span>
              <Button
                onClick={handleReset}
                style={{
                  background: "white",
                  color: "#333",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontWeight: "500",
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </div>
          </Card.Title>

          {hasSynthetic && (
            <Alert variant="warning" className="mb-3">
              One or more rows are <strong>SYNTHETIC</strong> staging fixtures — not physical
              device playback.
            </Alert>
          )}

          {isError && (
            <Alert variant="danger" className="mb-3">
              Could not load live plays
              {error && "status" in (error as object)
                ? ` (HTTP ${(error as { status?: number }).status})`
                : ""}
              . Backend authorization and staging projection must be available.
            </Alert>
          )}

          <BootstrapRow className="mb-4">
            <BootstrapCol md={3}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{metrics.activeDevices}</div>
                <div className={styles.statLabel}>Active Devices</div>
              </div>
            </BootstrapCol>
            <BootstrapCol md={3}>
              <div
                className={styles.statCard}
                style={{ borderColor: metrics.nowPlaying > 0 ? "#e21e26" : undefined }}
              >
                <div
                  className={styles.statValue}
                  style={{ color: metrics.nowPlaying > 0 ? "#e21e26" : undefined }}
                >
                  {metrics.nowPlaying > 0 && (
                    <PlayCircleOutlined spin style={{ marginRight: 8 }} />
                  )}
                  {metrics.nowPlaying}
                </div>
                <div className={styles.statLabel}>Now Playing</div>
              </div>
            </BootstrapCol>
            <BootstrapCol md={3}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{metrics.uniqueArtists}</div>
                <div className={styles.statLabel}>Unique Artists</div>
              </div>
            </BootstrapCol>
            <BootstrapCol md={3}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{metrics.activeChannels}</div>
                <div className={styles.statLabel}>Active Channels</div>
              </div>
            </BootstrapCol>
          </BootstrapRow>

          <Form layout="vertical" className={styles.form}>
            <BootstrapRow className="mb-2">
              <BootstrapCol md={2} lg={2}>
                <FormItem label="Brand" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`brand-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="All Brands"
                    value={filters.brandId}
                    optionFilterProp="label"
                    onChange={(value) => handleHierarchyChange("brandId", value)}
                    options={(filterOptions?.brands || []).map((b) => ({
                      label: b.name,
                      value: b.id,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={2} lg={2}>
                <FormItem label="Client" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`client-reset-${resetCounter}-${filters.brandId || "all"}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="All Clients"
                    value={filters.clientId}
                    optionFilterProp="label"
                    onChange={(value) => handleHierarchyChange("clientId", value)}
                    options={(filterOptions?.clients || []).map((c) => ({
                      label: c.name,
                      value: c.id,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={3} lg={3}>
                <FormItem label="Location" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`location-reset-${resetCounter}-${filters.brandId || "all"}-${filters.clientId || "all"}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="All Locations"
                    value={filters.locationId}
                    optionFilterProp="label"
                    onChange={(value) => handleHierarchyChange("locationId", value)}
                    options={(filterOptions?.locations || []).map((l) => ({
                      label: l.name,
                      value: l.id,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={2} lg={2}>
                <FormItem label="Zone" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`zone-reset-${resetCounter}-${filters.locationId || "all"}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="All Zones"
                    value={filters.zoneId}
                    optionFilterProp="label"
                    onChange={(value) => handleHierarchyChange("zoneId", value)}
                    options={(filterOptions?.zones || []).map((z) => ({
                      label: z.name,
                      value: z.id,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={3} lg={3}>
                <FormItem label="Device" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`device-reset-${resetCounter}-${filters.zoneId || "all"}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="All Devices"
                    value={filters.deviceId}
                    optionFilterProp="label"
                    onChange={(value) => handleHierarchyChange("deviceId", value)}
                    options={(filterOptions?.devices || []).map((d) => ({
                      label: d.name,
                      value: d.id,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
            </BootstrapRow>

            <BootstrapRow className="mb-2">
              <BootstrapCol md={3}>
                <FormItem label="Song" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`song-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="Any song"
                    value={filters.song}
                    onChange={(value) => handleFilterChange("song", value)}
                    options={(filterOptions?.songs || []).map((name) => ({
                      label: name,
                      value: name,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={3}>
                <FormItem label="Artist" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`artist-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="Any artist"
                    value={filters.artist}
                    onChange={(value) => handleFilterChange("artist", value)}
                    options={(filterOptions?.artists || []).map((name) => ({
                      label: name,
                      value: name,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={3}>
                <FormItem label="Channel" className="customSelectWrapper" layout="vertical">
                  <Select
                    key={`channel-reset-${resetCounter}`}
                    showSearch
                    allowClear
                    className="customSelect"
                    placeholder="Any channel"
                    value={filters.channel}
                    onChange={(value) => handleFilterChange("channel", value)}
                    options={(filterOptions?.channels || []).map((name) => ({
                      label: name,
                      value: name,
                    }))}
                  />
                </FormItem>
              </BootstrapCol>
              <BootstrapCol md={3}>
                <FormItem label="Date" className="customSelectWrapper" layout="vertical">
                  <DatePicker
                    key={`date-reset-${resetCounter}`}
                    allowClear
                    className="customSelect"
                    style={{ width: "100%" }}
                    placeholder="Select date"
                    value={filters.date ? dayjs(filters.date) : undefined}
                    onChange={(date) =>
                      handleFilterChange("date", date ? date.format("YYYY-MM-DD") : undefined)
                    }
                  />
                </FormItem>
              </BootstrapCol>
            </BootstrapRow>
          </Form>

          <Table
            className={`legacy-row-height-table ${styles.table}`}
            columns={columns}
            dataSource={plays}
            pagination={{ pageSize: 15, showQuickJumper: true }}
            rowKey="key"
            bordered
            scroll={{ x: true }}
            loading={{
              spinning: isFetching && plays.length === 0,
              indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
            }}
            locale={{
              emptyText: (
                <div style={{ padding: 40, textAlign: "center" }}>
                  <WifiOutlined style={{ fontSize: 48, color: "#ccc", marginBottom: 16 }} />
                  <div style={{ color: "#666", fontSize: 14 }}>
                    No playback events match the selected filters. Live plays appear when devices
                    emit authenticated playback events (or synthetic staging fixtures).
                  </div>
                </div>
              ),
            }}
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default withAuthenticationRequired(LivePlaysTracker);
